import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { Col, Row } from 'react-bootstrap'
import FullLogo from '../assets/logos/full-logo.png'
import ProfileAvatar from '../components/profileAvatar'
import theme from '../constants/theme'
import PrimaryButton from '../components/buttons/PrimaryButton'
import {
  ExternalLink,
  FooterContent,
  JobApplicationPositionCard,
  Typography,
  FullPageContainer,
  ScrollableContainer,
  StickyFooter,
  FooterText,
  InfoCard,
  AgencyBannerContainer,
  AgencyLogoContainer,
  AgencyLogoImage,
  AgencyTextContainer,
  AgencyLabelContainer,
  AgencyNameContainer,
} from '../components/styled'
import RecordFill from '../assets/svgs/recordFill'
import {
  seniorityLevel,
  employmentType,
  educationLevel,
  travelFreq,
  languages,
} from '../constants/position'
import {
  formatCountryName,
  formatWebsiteUrl,
} from '../utilities/stringUtilities'
import LanguagesDropdown from '../components/languageDropdwn'

function JobApplication({
  onApplyClick,
  company,
  position,
  isEasyApply = false,
}) {
  const { t, i18n } = useTranslation()
  const locale = i18n?.language
  const handleClick = () => {
    onApplyClick()
  }

  const mapValueToLabel = (constantsList, value) => {
    const selectedField = constantsList?.find((field) => field.value === value)
    return selectedField ? selectedField[locale] : ''
  }

  const getYearsOfExperienceText = () => {
    let yearsOfExperience = t('pages.minExpYears', {
      minYears: position?.yearsOfExperience,
    })
    if (position?.maxYearsOfExperience)
      yearsOfExperience = t('pages.minMaxExpYears', {
        minYears: position?.yearsOfExperience,
        maxYears: position?.maxYearsOfExperience,
      })
    if (position?.yearsOfExperience === 0)
      yearsOfExperience = t('pages.noExpNeeded')
    return <Typography size="13px">{yearsOfExperience}</Typography>
  }

  const positionTextLabels = [
    {
      value: mapValueToLabel(languages, position?.language),
      label: t('pages.language'),
    },
    {
      value: mapValueToLabel(educationLevel, position?.educationLevel),
      label: t('pages.educationLevel'),
    },
    {
      value: mapValueToLabel(travelFreq, position?.travelFreq),
      label: t('pages.travelFreq'),
    },
    {
      value: formatCountryName(position?.country),
      label: t('pages.location'),
    },
    !position?.hideSalary && {
      value: position?.budget + ' ' + position?.currency,
      label: t('pages.salary'),
    },
  ].filter(Boolean)

  const positionArrayLabels = [
    { value: 'certificates', label: t('pages.certificates') },
    { value: 'knowledge', label: t('pages.knowledge') },
    { value: 'licenses', label: t('pages.licenses') },
    { value: 'skills', label: t('pages.skills') },
  ]

  const getApplyButtonText = () => {
    if (isEasyApply) return t('pages.easyApply')
    if (position?.isInternalApply) return t('pages.applyWithCompanyID')
    return t('pages.apply')
  }

  return (
    <FullPageContainer>
      <ScrollableContainer className="mt-3 fw-light">
        <Row className="my-2 align-items-end">
          <Col xs="auto">
            <img height="auto" width="150px" src={FullLogo} alt="" />
          </Col>
          <Col />
          <Col xs="auto">
            <LanguagesDropdown />
          </Col>
        </Row>
        <Row>
          <JobApplicationPositionCard
            className="h-100 px-4 mb-5"
            style={{
              boxShadow: '0px 15px 20px 0px rgba(18, 38, 63, 0.15)',
              overflowY: 'auto',
              maxHeight: '70vh',
            }}
          >
            {position?.agency && (
              <Row className="mx-0 mt-4 mb-3">
                <Col className="px-0">
                  <AgencyBannerContainer>
                    <AgencyLogoContainer>
                      {position.agency?.logo ? (
                        <AgencyLogoImage
                          src={position.agency.logo}
                          alt={position.agency.name}
                        />
                      ) : (
                        <ProfileAvatar name={position.agency.name} size={56} />
                      )}
                    </AgencyLogoContainer>

                    <AgencyTextContainer>
                      <AgencyLabelContainer>
                        <Typography
                          size="11px"
                          regular
                          style={{
                            color: theme.talentinoSecondaryGray,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            fontWeight: '500',
                          }}
                        >
                          {t('pages.recruitmentPartner')}
                        </Typography>
                      </AgencyLabelContainer>
                      <AgencyNameContainer>
                        <Typography
                          size="16px"
                          bold
                          style={{
                            color: theme.talentinoBlack,
                            lineHeight: '1.3',
                          }}
                        >
                          {position.agency.name}
                        </Typography>
                      </AgencyNameContainer>
                      <div>
                        <Typography
                          size="12px"
                          regular
                          style={{
                            color: theme.talentinoSecondaryGray,
                            lineHeight: '1.4',
                          }}
                        >
                          {t('pages.agencyTagline')}
                        </Typography>
                      </div>
                    </AgencyTextContainer>
                  </AgencyBannerContainer>
                </Col>
              </Row>
            )}

            <Row className="mx-0 mt-3 mb-3">
              <Col
                className="px-0 d-flex justify-content-between align-items-center flex-wrap"
                style={{ gap: '16px' }}
              >
                <Typography
                  bold
                  size="19px"
                  color={theme.talentinoBlack}
                  dangerouslySetInnerHTML={{
                    __html: t('pages.companyHiringPosition', {
                      company:
                        company?.showClientDetails !== false
                          ? company?.companyName
                          : t('organization.oneOfOurClientsIsHiring'),
                      position: position?.title,
                    }),
                  }}
                />
                <PrimaryButton
                  backgroundcolor={theme.brandColorOrange}
                  hovercolor={theme.talentinoDarkerBlue}
                  onClick={handleClick}
                >
                  {getApplyButtonText()}
                </PrimaryButton>
              </Col>
            </Row>
            <Row
              id="position-info"
              className="mb-3 px-2"
              style={{ marginLeft: '-20px', marginRight: '-20px' }}
            >
              <Col
                id="position-job-info"
                className="lh-sm mb-3 mb-lg-0"
                xs={12}
                lg={6}
                style={{ paddingLeft: '20px', paddingRight: '20px' }}
              >
                <Row id="job-title">
                  <InfoCard className="px-4 py-3">
                    <div
                      className="d-flex justify-content-between align-items-center flex-wrap"
                      style={{ gap: '12px' }}
                    >
                      <Typography bold size="20px" color={theme.talentinoBlack}>
                        {position.title}
                      </Typography>
                      <div
                        className="d-flex"
                        style={{ gap: '8px', flexWrap: 'wrap' }}
                      >
                        <span
                          style={{
                            padding: '4px 12px',
                            borderRadius: '50px',
                            backgroundColor: theme.talentinoWhiteBlue,
                            color: theme.talentinoBlue,
                            fontSize: '12px',
                            fontWeight: '500',
                          }}
                        >
                          {mapValueToLabel(
                            employmentType,
                            position?.employmentType,
                          )}
                        </span>
                        <span
                          style={{
                            padding: '4px 12px',
                            borderRadius: '50px',
                            backgroundColor: theme.talentinoWhiteBlue,
                            color: theme.talentinoBlue,
                            fontSize: '12px',
                            fontWeight: '500',
                          }}
                        >
                          {mapValueToLabel(
                            seniorityLevel,
                            position?.seniorityLevel,
                          )}
                        </span>
                      </div>
                    </div>
                  </InfoCard>
                </Row>
                {company?.showClientDetails !== false ||
                position?.workPlaceType ? (
                  <Row id="job-title-info" className="py-2">
                    <Typography
                      className="d-flex justify-content-start"
                      size="12px"
                    >
                      {company?.showClientDetails !== false && (
                        <div className="px-2">{company?.companyName}</div>
                      )}
                      {position?.workPlaceType && (
                        <div className="px-2">
                          <RecordFill width={16} height={16} className="mr-2" />{' '}
                          <span className="text-primary">
                            {position?.workPlaceType}
                          </span>
                        </div>
                      )}
                    </Typography>
                  </Row>
                ) : (
                  <br />
                )}
                <Row>
                  <InfoCard className="px-4 py-3 overflow-hidden">
                    <Typography color={theme.talentinoBlue} size="15px" bold>
                      {t('pages.jobDescription')}
                    </Typography>
                    <Typography className="mt-2 mb-3" bold size="14px">
                      {t('pages.responsibilities')}:
                    </Typography>
                    <div
                      className="fw-light"
                      dangerouslySetInnerHTML={{
                        __html: position?.description,
                      }}
                    />
                  </InfoCard>
                </Row>
              </Col>
              <Col
                id="company-details"
                className="lh-sm"
                xs={12}
                lg={6}
                style={{ paddingLeft: '20px', paddingRight: '20px' }}
              >
                <Row>
                  <InfoCard className="px-4 py-3 overflow-hidden">
                    <Typography
                      className="mb-2"
                      bold
                      size="15px"
                      color={theme.talentinoBlue}
                    >
                      {t('pages.jobRequirements')}
                    </Typography>
                    <Typography
                      size="13px"
                      id="company-details"
                      className="lh-sm"
                      regular
                    >
                      <div className="">
                        {getYearsOfExperienceText()}
                        {positionTextLabels?.map((key) => (
                          <div key={key.value}>
                            <Typography>{key.label}: </Typography>
                            <Typography regular>{key.value}</Typography>
                          </div>
                        ))}
                        {positionArrayLabels?.map((arrayKey) =>
                          position[arrayKey.value] &&
                          position[arrayKey.value].length ? (
                            <div key={arrayKey.value}>
                              <Typography>{arrayKey.label}:</Typography>
                              {position[arrayKey.value].map((key, index) => (
                                <Typography key={key} className="mr-2" regular>
                                  {key}
                                  {index < position[arrayKey.value].length - 1
                                    ? ', '
                                    : ''}
                                </Typography>
                              ))}
                            </div>
                          ) : null,
                        )}
                      </div>
                      <div className="mt-2">
                        <Typography>{t('pages.requirements')}</Typography>
                      </div>
                    </Typography>
                    <div
                      className="fw-light"
                      dangerouslySetInnerHTML={{
                        __html: position?.requirements,
                      }}
                    />
                  </InfoCard>
                </Row>
                {company?.showClientDetails !== false && (
                  <Row className="mt-3">
                    <InfoCard className="px-4 py-3">
                      <Row className="d-flex align-items-center">
                        <Col>
                          <Typography
                            className="fw-normal mb-2"
                            color={theme.talentinoBlue}
                            size="15px"
                            bold
                          >
                            {company.companyName}
                          </Typography>
                          <Typography size="13px" className="lh-sm" regular>
                            {company?.workingLocations &&
                              company.workingLocations.length > 0 && (
                                <div className="mt-2">
                                  {t('general.location')}:
                                  {company.workingLocations.map(
                                    (location, index) => (
                                      <span className="mx-1" key={location}>
                                        {formatCountryName(location)}
                                        {index <
                                          company.workingLocations.length - 1 &&
                                          ' ,'}
                                      </span>
                                    ),
                                  )}
                                </div>
                              )}
                            {company.website && (
                              <div className="d-fex">
                                {t('general.website')}:{' '}
                                <ExternalLink
                                  href={formatWebsiteUrl(company.website)}
                                  target="_blank"
                                >
                                  {company.website}
                                </ExternalLink>
                              </div>
                            )}
                            {company.industry && (
                              <div>
                                {t('card.industry')}: {company.industry}
                              </div>
                            )}
                            {company.companySize && (
                              <div>
                                {t('card.companySize')}: {company.companySize}
                              </div>
                            )}
                          </Typography>
                        </Col>
                        <Col sm="auto" className="mb-auto">
                          {company?.logo ? (
                            <img
                              src={company.logo}
                              alt={`${company.companyName} logo`}
                              className="img-fluid"
                              style={{ maxHeight: '60px', maxWidth: '100%' }}
                            />
                          ) : (
                            <ProfileAvatar name={company.companyName} />
                          )}
                        </Col>
                      </Row>
                    </InfoCard>
                  </Row>
                )}
                {position?.agency && !company?.isAgency && (
                  <Row className="mt-3">
                    <InfoCard className="px-4 py-3">
                      <Row className="d-flex align-items-center">
                        <Col>
                          <AgencyLabelContainer>
                            <Typography
                              size="11px"
                              regular
                              style={{
                                color: theme.talentinoSecondaryGray,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                fontWeight: '500',
                              }}
                            >
                              {t('pages.aboutRecruitmentPartner')}
                            </Typography>
                          </AgencyLabelContainer>
                          <Typography
                            className="fw-normal mb-2"
                            color={theme.black}
                            size="15px"
                            bold
                          >
                            {position?.agency?.name}
                          </Typography>
                          <Typography size="13px" className="lh-sm" regular>
                            <div className="mt-2">
                              {t('general.location')}:
                              {position?.agency?.workingLocations?.map(
                                (location, index) => (
                                  <span className="mx-1" key={location}>
                                    {formatCountryName(location)}
                                    {index <
                                      position?.agency?.workingLocations
                                        ?.length -
                                        1 && ' ,'}
                                  </span>
                                ),
                              )}
                            </div>
                            {position?.agency?.website && (
                              <div className="d-fex">
                                {t('general.website')}:{' '}
                                <ExternalLink
                                  href={formatWebsiteUrl(
                                    position?.agency?.website,
                                  )}
                                  target="_blank"
                                >
                                  {position?.agency?.website}
                                </ExternalLink>
                              </div>
                            )}
                          </Typography>
                        </Col>
                        <Col sm="auto" className="mb-auto">
                          {position?.agency?.logo ? (
                            <img
                              src={position?.agency?.logo}
                              alt={`${position?.agency?.name} logo`}
                              className="img-fluid"
                              style={{ maxHeight: '60px', maxWidth: '100%' }}
                            />
                          ) : (
                            <ProfileAvatar name={position?.agency?.name} />
                          )}
                        </Col>
                      </Row>
                    </InfoCard>
                  </Row>
                )}
              </Col>
            </Row>
          </JobApplicationPositionCard>
        </Row>
      </ScrollableContainer>

      <StickyFooter>
        <FooterText>
          <span style={{ fontWeight: '600' }}>
            {t('footer.TalentinoAI')}{' '}
            <FooterContent>© {new Date().getFullYear()}</FooterContent>
          </span>
          .{' '}
          <span style={{ fontWeight: '600' }}>
            {t('footer.allRightsReserved')}
          </span>
        </FooterText>
      </StickyFooter>
    </FullPageContainer>
  )
}

JobApplication.propTypes = {
  onApplyClick: PropTypes.func.isRequired,
  isEasyApply: PropTypes.bool,
  company: PropTypes.shape({
    companyName: PropTypes.string,
    website: PropTypes.string,
    workingLocations: PropTypes.arrayOf(PropTypes.string),
    address: PropTypes.string,
    industry: PropTypes.string,
    logo: PropTypes.string,
    showClientDetails: PropTypes.bool,
    companySize: PropTypes.string,
    isAgency: PropTypes.bool,
  }).isRequired,
  position: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    requirements: PropTypes.string,
    employmentType: PropTypes.string,
    isInternalApply: PropTypes.bool,
    seniorityLevel: PropTypes.string,
    workPlaceType: PropTypes.string,
    language: PropTypes.string,
    educationLevel: PropTypes.string,
    yearsOfExperience: PropTypes.number,
    maxYearsOfExperience: PropTypes.number,
    country: PropTypes.string,
    travelFreq: PropTypes.string,
    hideSalary: PropTypes.bool,
    currency: PropTypes.string,
    budget: PropTypes.string,
    agency: PropTypes.shape({
      name: PropTypes.string,
      logo: PropTypes.string,
      workingLocations: PropTypes.arrayOf(PropTypes.string),
      website: PropTypes.string,
    }),
  }).isRequired,
}

export default JobApplication
